import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription, timer } from 'rxjs';
import { switchMap, catchError, takeWhile, tap } from 'rxjs/operators';
import { of } from 'rxjs';

import { CardapioCobrancaQrDTO, CardapioTrackingDTO } from '../../../../models/cardapio-digital/cardapio-digital.model';
import { TipoPedido } from '../../../../enums/TipoPedido.enum';
import { formatarPreco as formatarPrecoUtil } from '../../utils/cardapio-preco.util';
import { CardapioPublicoService } from '../../../../services/cardapio-digital/cardapio-publico.service';
import { CardapioHistoricoService } from '../../../../services/cardapio-digital/cardapio-historico.service';

// Estados considerados finais — polling deve parar apos atingi-los
// Fonte: API_CONTRACT_pedido_cardapio_digital.md
const ESTADOS_FINAIS = ['FINALIZADO', 'CANCELADO'];

// Intervalo de polling em milissegundos
const POLLING_INTERVALO_MS = 15000;

// Mapeamento: indice da etapa ativa para cada estado do backend (por tipo de pedido)
// O indice representa a ultima etapa ATIVA (todas as anteriores tambem ficam ativas)
// Estados possiveis vindos do backend (EstadoDePedido.kt):
//   ABERTO | EM_PRODUCAO | A_CAMINHO | AGUARDANDO_RETIRADA | ENTREGUE | FINALIZADO | CANCELADO
// Nota: o API_CONTRACT documenta 4 estados simplificados, mas o backend retorna o .name() real do enum.
const ETAPA_POR_ESTADO_DELIVERY: Record<string, number> = {
  ABERTO: 0,
  EM_PRODUCAO: 1,
  A_CAMINHO: 2,
  AGUARDANDO_RETIRADA: 1,
  ENTREGUE: 3,
  FINALIZADO: 3,
  CANCELADO: 0,
  // Compatibilidade: caso o backend venha a retornar o estado simplificado no futuro
  EM_ANDAMENTO: 1,
};

const ETAPA_POR_ESTADO_MESA: Record<string, number> = {
  ABERTO: 0,
  EM_PRODUCAO: 1,
  A_CAMINHO: 1,
  AGUARDANDO_RETIRADA: 1,
  ENTREGUE: 2,
  FINALIZADO: 2,
  CANCELADO: 0,
  EM_ANDAMENTO: 1,
};

const ETAPA_POR_ESTADO_RETIRADA: Record<string, number> = {
  ABERTO: 0,
  EM_PRODUCAO: 1,
  A_CAMINHO: 1,
  AGUARDANDO_RETIRADA: 2,
  ENTREGUE: 2,
  FINALIZADO: 2,
  CANCELADO: 0,
  EM_ANDAMENTO: 1,
};

@Component({
  selector: 'app-cardapio-tracking',
  templateUrl: './cardapio-tracking.component.html',
  styleUrls: ['./cardapio-tracking.component.scss'],
})
export class CardapioTrackingComponent implements OnInit, OnDestroy {
  @Input() tracking!: CardapioTrackingDTO;

  /**
   * Cobranca por QR, quando o cliente escolheu cartao ou QR Code. Nulo nas demais formas —
   * dinheiro e transferencia sao acertados direto com a loja.
   */
  @Input() cobrancaQr: CardapioCobrancaQrDTO | null = null;
  @Input() moedaSimbolo = 'BRL';
  @Input() moedaLocal = 'pt-BR';
  @Input() carrinhoTemItens = false;
  /** Slug do estabelecimento necessario para o polling */
  @Input() slug: string | null = null;
  /** Indica se o tracking foi aberto a partir do historico de pedidos */
  @Input() veioDoHistorico = false;
  @Output() voltarAoCardapio = new EventEmitter<void>();
  @Output() voltarDoHistorico = new EventEmitter<void>();
  @Output() copiarPedido = new EventEmitter<CardapioTrackingDTO>();

  texts: any = {};

  // Controla a visibilidade do modal "Quase pronto..."
  modalWhatsappVisivel = true;

  // Controla a visibilidade do modal de copiar pedido
  modalCopiarVisivel = false;

  private subs: Subscription[] = [];

  constructor(
    private translate: TranslateService,
    private cardapioPublicoService: CardapioPublicoService,
    private historicoService: CardapioHistoricoService,
  ) {}

  ngOnInit(): void {
    this.loadTranslations();
    this.subs.push(
      this.translate.onLangChange.subscribe(() => this.loadTranslations())
    );
    // Rolar para o topo ao exibir o tracking
    window.scrollTo(0, 0);

    // Iniciar polling apenas quando ha slug e codigoDePedido validos
    this.iniciarPolling();
  }

  get isDelivery(): boolean {
    return this.tracking?.tipoPedido === TipoPedido.DELIVERY;
  }

  get isMesa(): boolean {
    return this.tracking?.tipoPedido === TipoPedido.MESA;
  }

  get isDinheiro(): boolean {
    return this.tracking?.formaPagamento === 'DINHEIRO';
  }

  get temTroco(): boolean {
    return this.isDinheiro && this.tracking?.troco !== undefined && this.tracking.troco > 0;
  }

  /**
   * Retorna o indice da ultima etapa ativa com base no estado atual do pedido.
   * Todas as etapas ate esse indice (inclusive) serao marcadas como ativas.
   */
  private get indiceEtapaAtiva(): number {
    const estado = this.tracking?.estadoDePedido || 'ABERTO';

    if (this.isDelivery) {
      return ETAPA_POR_ESTADO_DELIVERY[estado] ?? 0;
    }
    if (this.isMesa) {
      return ETAPA_POR_ESTADO_MESA[estado] ?? 0;
    }
    // Retirada
    return ETAPA_POR_ESTADO_RETIRADA[estado] ?? 0;
  }

  /** Etapas de acompanhamento do pedido com estado reativo ao backend */
  get etapas(): { label: string; icone: string; ativa: boolean }[] {
    const etapaAtiva = this.indiceEtapaAtiva;

    if (this.isDelivery) {
      return [
        { label: this.texts.tracking?.etapa_recebido || 'Pedido recebido', icone: 'pi-check-circle', ativa: etapaAtiva >= 0 },
        { label: this.texts.tracking?.etapa_preparo || 'Em preparo', icone: 'pi-cog', ativa: etapaAtiva >= 1 },
        { label: this.texts.tracking?.etapa_entrega || 'Saiu para entrega', icone: 'pi-truck', ativa: etapaAtiva >= 2 },
        { label: this.texts.tracking?.etapa_entregue || 'Entregue', icone: 'pi-home', ativa: etapaAtiva >= 3 },
      ];
    }
    if (this.isMesa) {
      return [
        { label: this.texts.tracking?.etapa_recebido || 'Pedido recebido', icone: 'pi-check-circle', ativa: etapaAtiva >= 0 },
        { label: this.texts.tracking?.etapa_preparo || 'Em preparo', icone: 'pi-cog', ativa: etapaAtiva >= 1 },
        { label: this.texts.tracking?.etapa_pronto || 'Pronto', icone: 'pi-star', ativa: etapaAtiva >= 2 },
      ];
    }
    // Retirada
    return [
      { label: this.texts.tracking?.etapa_recebido || 'Pedido recebido', icone: 'pi-check-circle', ativa: etapaAtiva >= 0 },
      { label: this.texts.tracking?.etapa_preparo || 'Em preparo', icone: 'pi-cog', ativa: etapaAtiva >= 1 },
      { label: this.texts.tracking?.etapa_pronto_retirada || 'Pronto para retirada', icone: 'pi-shopping-bag', ativa: etapaAtiva >= 2 },
    ];
  }

  /**
   * Inicia o polling periodico para atualizar o estado do pedido.
   * So executa quando ha slug e codigoDePedido validos.
   * Para automaticamente ao atingir um estado final.
   *
   * Usa takeWhile no pipe para evitar referencia circular com pollingSub:
   * o padrao anterior com timer(0, ...) causava unsubscribe() antes de pollingSub
   * ser atribuido (closure captura variavel ainda nao inicializada no primeiro tick),
   * silenciando o polling e impedindo a atualizacao de estado na tela do cliente.
   */
  private iniciarPolling(): void {
    if (!this.slug || !this.tracking?.codigoDePedido) return;

    // Estado inicial ja pode ser final (ex: pedido do historico ja entregue)
    if (this.estadoEhFinal()) return;

    const slug = this.slug;
    const codigoDePedido = this.tracking.codigoDePedido;

    const slugHistorico = this.slug || 'preview';

    const pollingSub = timer(0, POLLING_INTERVALO_MS).pipe(
      switchMap(() =>
        this.cardapioPublicoService.consultarStatusPedido(slug, codigoDePedido).pipe(
          catchError(() => of(null))
        )
      ),
      tap(resposta => {
        if (!resposta) return;
        const novoEstado = resposta.estadoDePedido;
        this.tracking = { ...this.tracking, estadoDePedido: novoEstado };

        // Atualizar o historico local com o novo estado e limpar pendente se for final
        this.historicoService.atualizarEstadoPedido(slugHistorico, this.tracking.idCurto, novoEstado);
      }),
      // Para o Observable quando o estado for final (inclusivo: processa o ultimo valor antes de parar)
      takeWhile(() => !this.estadoEhFinal(), true)
    ).subscribe();

    this.subs.push(pollingSub);
  }

  private estadoEhFinal(): boolean {
    return ESTADOS_FINAIS.includes(this.tracking?.estadoDePedido || '');
  }

  get labelTipoPedido(): string {
    switch (this.tracking?.tipoPedido) {
      case TipoPedido.DELIVERY: return this.texts.tracking?.tipo_delivery || 'Delivery';
      case TipoPedido.MESA: return this.texts.tracking?.tipo_mesa || 'No local';
      case TipoPedido.RETIRADA: return this.texts.tracking?.tipo_retirada || 'Retirada';
      default: return '';
    }
  }

  formatarPreco(valor: number): string {
    return formatarPrecoUtil(valor, this.moedaSimbolo, this.moedaLocal);
  }

  onVoltarAoCardapio(): void {
    this.voltarAoCardapio.emit();
  }

  onVoltarDoHistorico(): void {
    this.voltarDoHistorico.emit();
  }

  fecharModalWhatsapp(): void {
    this.modalWhatsappVisivel = false;
  }

  abrirModalCopiar(): void {
    this.modalCopiarVisivel = true;
  }

  fecharModalCopiar(): void {
    this.modalCopiarVisivel = false;
  }

  confirmarCopiarPedido(): void {
    this.modalCopiarVisivel = false;
    this.copiarPedido.emit(this.tracking);
  }

  /** Feedback do botao de copiar o payload do QR; volta ao normal sozinho. */
  qrCopiado = false;

  /**
   * Copia o payload EMV do QR para o cliente colar no app do banco.
   *
   * Existe porque a imagem do QR depende da Bancard responder e de o cliente conseguir apontar
   * a camera para a propria tela — o que nao da para fazer quando ele ja esta no celular.
   */
  copiarCodigoQr(): void {
    const codigo = this.cobrancaQr?.qrData;
    if (!codigo) return;

    const marcarCopiado = () => {
      this.qrCopiado = true;
      setTimeout(() => (this.qrCopiado = false), 2000);
    };

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(codigo).then(marcarCopiado).catch(() => this.copiarComFallback(codigo, marcarCopiado));
      return;
    }
    this.copiarComFallback(codigo, marcarCopiado);
  }

  /** Safari antigo e contexto sem HTTPS nao tem `navigator.clipboard`. */
  private copiarComFallback(texto: string, onSucesso: () => void): void {
    const campo = document.createElement('textarea');
    campo.value = texto;
    campo.style.position = 'fixed';
    campo.style.opacity = '0';
    document.body.appendChild(campo);
    campo.select();
    try {
      document.execCommand('copy');
      onSucesso();
    } finally {
      document.body.removeChild(campo);
    }
  }

  onEnviarWhatsapp(): void {
    if (this.tracking?.linkWhatsapp) {
      window.open(this.tracking.linkWhatsapp, '_blank');
    }
    this.modalWhatsappVisivel = false;
  }

  private loadTranslations(): void {
    this.texts = this.translate.instant('cardapio_digital');
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
  }
}
